import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Upload } from 'lucide-react';
import FloorPlanPdfCropModal from './FloorPlanPdfCropModal';
import { isFloorPlanImageFile, isPdfFile, prepareFloorPlanImageFile } from '../utils/floorPlanPdf';

const ACCEPT = 'image/*,.pdf,application/pdf';

/**
 * Hidden file input + button. Images upload directly; PDF opens crop modal → image.
 */
export default function FloorPlanUploadButton({
  onFileReady,
  disabled = false,
  className = '',
  children,
  label = 'Upload',
  uploading = false,
}) {
  const inputRef = useRef(null);
  const [pdfFile, setPdfFile] = useState(null);

  const handleRawFile = async (file) => {
    if (!file) return;
    if (isPdfFile(file)) {
      setPdfFile(file);
      return;
    }
    if (isFloorPlanImageFile(file)) {
      try {
        const prepared = await prepareFloorPlanImageFile(file);
        onFileReady(prepared);
      } catch (e) {
        toast.error(e?.message || 'Could not prepare image');
      }
      return;
    }
    toast.error('Please choose an image or PDF');
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          handleRawFile(f);
        }}
      />
      <button
        type="button"
        disabled={disabled || uploading}
        onClick={() => inputRef.current?.click()}
        className={className}
      >
        {children ?? (
          <>
            <Upload className="w-3.5 h-3.5 mr-1" />
            {uploading ? '…' : label}
          </>
        )}
      </button>
      {pdfFile && (
        <FloorPlanPdfCropModal
          file={pdfFile}
          onClose={() => setPdfFile(null)}
          onConfirm={(imgFile) => {
            setPdfFile(null);
            onFileReady(imgFile);
          }}
        />
      )}
    </>
  );
}

export { ACCEPT as FLOOR_PLAN_UPLOAD_ACCEPT };
